// Implements: .claude/skills/rehaut-design/ui_kits/rehaut-pacientes/primitives.jsx (SectionLabel)
// See: .claude/skills/rehaut-design/HANDOFF.md
'use client';

import type { ReactNode } from 'react';

/**
 * Sticky section header used in the detail pane. 10px tracked
 * uppercase label on the left, optional mono `hint` on the right
 * ("6 eventos", "2 activos"). Sticks to the top of the scroll
 * container as a SOLID band (rows scrolling underneath must not show
 * through the label). With `fade`, a 12px gradient also hangs below the
 * band; it paints over whatever follows the label even at rest, so only
 * scrollers that want it (the /pacientes document) opt in.
 *
 * The band colour is `--section-band` (default `--surface`): a pane whose
 * own background differs (the /pacientes document sits on cream `--bg`)
 * sets the variable once on its scroller so the band matches it.
 */
export function SectionLabel({
  children,
  hint,
  action,
  fade = false,
  sticky = true,
}: {
  children: ReactNode;
  hint?: ReactNode;
  /** Optional inline action (e.g. "+ Nueva nota" button). Rendered with
   *  letter-spacing: 0 + text-transform: none so it doesn't inherit the
   *  uppercase chrome of the label. */
  action?: ReactNode;
  /** Hang a 12px fade below the sticky band. Off by default: at rest it
   *  covers the top of the next section. */
  fade?: boolean;
  /** Stick to the top of the scroller (default). A short section (one or
   *  two cards, e.g. Paquetes) passes false: stuck, its band sat over the
   *  first line of the card sliding under it for the whole section
   *  (round-6 iPad #6), and it scrolls past too fast to need a pinned
   *  title. */
  sticky?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        padding: '20px 28px 8px',
        ...(sticky ? { position: 'sticky' as const, top: 0 } : { position: 'relative' as const }),
        background: 'var(--section-band, var(--surface))',
        zIndex: 1,
        gap: '12px',
      }}
    >
      {fade && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: '100%',
            height: 12,
            background: 'linear-gradient(180deg, var(--section-band, var(--surface)), transparent)',
            pointerEvents: 'none',
          }}
        />
      )}
      <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: '12px', minWidth: 0 }}>
        {/* One line only: wrapped, "HISTORIA DE / TRATAMIENTO" grew past
            the band beside "+ Nueva nota" and the hint. */}
        <div
          style={{
            fontSize: '10.5px',
            fontWeight: 500,
            letterSpacing: '1.2px',
            textTransform: 'uppercase',
            color: 'var(--ink-4)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
          }}
        >
          {children}
        </div>
        {action && (
          <span
            style={{
              flexShrink: 0,
              letterSpacing: 0,
              textTransform: 'none',
              fontWeight: 400,
              fontSize: '11px',
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            {action}
          </span>
        )}
      </div>
      {hint && (
        <div
          style={{
            fontSize: '11px',
            color: 'var(--ink-4)',
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}
